import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuthCheck } from '../context/authkeyCheck';
import { ServerURL } from '../serverURL';

const UnreadCountContext = createContext(null);

export const useUnreadCountCheck = () => useContext(UnreadCountContext);

export const UnreadCountCheckProvider = ({ children }) => {
    const location = useLocation();
    const { isCompanyAuthenticated, companyAuthKey, isAdminAuthenticated, adminAuthKey } = useAuthCheck();
    const [scoutPendingActions, setScoutPendingActions] = useState([]);
    const [isAlertModalCount, setIsAlertModalCount] = useState(0);
    const [scoutId, setScoutId] = useState(null);
    const [unreadSecretEntryCount, setUnreadSecretEntryCount] = useState(0);
    const [unreadScoutCount, setUnreadScoutCount] = useState(0);
    const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
    const [unreadChatCount, setUnreadChatCount] = useState(0);
    const [headerUnreadChatCounts, setHeaderUnreadChatCounts] = useState({});
    const [showBadge, setShowBadge] = useState(false);

    const [unassigned_job_consultant_request_count, setunassigned_job_consultant_request_count] = useState(0);
    const [unassigned_scout_pending_count, setunassigned_scout_pending_count] = useState(0);
    const [unassigned_companies_count, setunassigned_companies_count] = useState(0);
    const [unassigned_jobs_count, setunassigned_jobs_count] = useState(0);
    const [unread_hiring_count, setunread_hiring_count] = useState(0);
    const [unread_scouting_count, setunread_scouting_count] = useState(0);

    const [isViewedByCompany, setIsViewedByCompany] = useState('No');
    const [socutUnreadCount, setSocutUnreadCount] = useState(0);
    const [hiringUnreadCount, setHiringUnreadCount] = useState(0);

    const [notificationsLists, setNotificationsLists] = useState([]);
    const [notificationListsError, setNotificationListsError] = useState('');

    useEffect(() => {
        if ((location.pathname.startsWith('/company'))) {
            if (isCompanyAuthenticated && companyAuthKey) {
                companyTriggerAllUnreadCounts(companyAuthKey);
            }
        }
        else if ((location.pathname.startsWith('/admin'))) {
            if (isAdminAuthenticated && adminAuthKey) {
                adminTriggerAllUnreadCounts(adminAuthKey);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isCompanyAuthenticated, companyAuthKey, isAdminAuthenticated, adminAuthKey, location]);

    // Admin trigger all unread counts
    const adminTriggerAllUnreadCounts = async (adminAuthKey) => {
        await Promise.all([
            fetchAdminHeaderCounts(adminAuthKey)
        ]);
    };

    //handle button clicks and page refresh call API
    const handleAdminRefreshUnreadCountAPI = () => {
        if (isAdminAuthenticated && adminAuthKey) {
            adminTriggerAllUnreadCounts(adminAuthKey);
        }
    };

    // Admin API call functions
    const fetchAdminHeaderCounts = async (adminAuthKey) => { //Admin header unread counts
        let data;
        try {
            const response = await fetch(`${ServerURL.PRODUCTION_HOST_URL}/agent/get_header_counts`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${adminAuthKey}`,
                },
            });
            data = await response.json();
            if (!response.ok) throw new Error(data);
            setunassigned_job_consultant_request_count(data?.header_counts?.unassigned_job_consultant_request_count || 0);
            setunassigned_scout_pending_count(data?.header_counts?.unassigned_scout_pending_count || 0);
            setunassigned_companies_count(data?.header_counts?.unread_companies_count || 0);
            setunassigned_jobs_count(data?.header_counts?.unread_jobs_count || 0);
            setunread_scouting_count(data?.header_counts?.unread_scouting_count || 0);
            setunread_hiring_count(data?.header_counts?.unread_hiring_count || 0);
        } catch (error) {
            setunassigned_job_consultant_request_count(0);
            setunassigned_scout_pending_count(0);
            setunassigned_companies_count(0);
            setunassigned_jobs_count(0);
        }
    };

    // Company trigger all unread counts
    const companyTriggerAllUnreadCounts = async (companyAuthKey) => {
        await Promise.all([
            fetchScoutPendingActions(companyAuthKey),
            fetchUnreadChatCounts(companyAuthKey),
            fetchUnreadNotificationCounts(companyAuthKey),
            fetchUnreadCounts(companyAuthKey),
            fetchUnreadCandidateCounts(companyAuthKey),
            fetchUnreadScoutCounts(companyAuthKey),
            fetchUnreadInvoiceCounts(companyAuthKey)
        ]);
    };

    //handle button clicks and page refresh call API
    const handleRefreshUnreadCountAPI = () => {
        if (isCompanyAuthenticated && companyAuthKey) {
            companyTriggerAllUnreadCounts(companyAuthKey);
        }
    };

    // Company API call function
    const fetchScoutPendingActions = async (authKey) => { //Scout pending actions
        let data;
        try {
            const response = await fetch(`${ServerURL.PRODUCTION_HOST_URL}companies/list_scout_pending_actions`, {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${authKey}`,
                },
            });
            data = await response.json();
            if (!response.ok) throw new Error(data);
            setScoutPendingActions(data.scout_action_pending_list);
            setIsAlertModalCount(data.scout_action_pending_list.length > 0 ? data.scout_action_pending_list.length : 0);
            if (data.scout_action_pending_list.length > 0) {
                sessionStorage.removeItem('DetailModalOpenFlag');
                setScoutId(data.scout_action_pending_list[0].scout_id);
            }
        } catch (error) {
            setScoutPendingActions([]);
            setIsAlertModalCount(0);
        }
    };

    // Company API call function
    const fetchUnreadChatCounts = async (authKey) => { //Unread chat conversation count
        let data;
        try {
            const response = await fetch(`${ServerURL.PRODUCTION_HOST_URL}/companies/unread_chat_conversation_count`, {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${authKey}`,
                },
            });
            data = await response.json();
            if (!response.ok) throw new Error(data);
            const unreadData = {};
            data.unread_chat_details.forEach((item) => {
            unreadData[item.progress_type] = item.unread_count;
            });

            setHeaderUnreadChatCounts(unreadData);
            setUnreadChatCount(data?.tot_unread_msgs || 0);
        } catch (error) {
            setUnreadChatCount(0);
            setHeaderUnreadChatCounts({});
        }
    };

    // Company API call function
    const fetchUnreadNotificationCounts = async (authKey) => { // Get notifiction lists 
        let data;
        try {
            const response = await fetch(`${ServerURL.PRODUCTION_HOST_URL}/companies/get_notification_list`, {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${authKey}`,
                },
            });
            data = await response.json();
            if (!response.ok) throw new Error(data);
            setNotificationsLists(data.notifications_list);
            setNotificationListsError('');
            setUnreadNotificationCount(data.stats?.unread_count || 0);
        } catch (error) {
            setNotificationsLists([]);
            setNotificationListsError(data.error.message);
            setUnreadNotificationCount(0);
        }
    };

    // Company API call function
    const fetchUnreadCounts = async (authKey) => { //Unread secret entry counts
        let data;
        try {
            const response = await fetch(`${ServerURL.PRODUCTION_HOST_URL}/companies/get_secret_entry_unread_counts`, {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${authKey}`,
                },
            });
            data = await response.json();
            if (!response.ok) throw new Error(data);
            setUnreadSecretEntryCount(data.secret_entry_stat_details?.secret_entry_unread_counts || 0);
        } catch (error) {
            setUnreadSecretEntryCount(0);
        }
    };

    // Company API call function
    const fetchUnreadCandidateCounts = async (authKey) => { //Unread added candidate counts
        let data;
        try {
            const response = await fetch(`${ServerURL.PRODUCTION_HOST_URL}/companies/get_added_candidate_unread_counts`, {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${authKey}`,
                },
            });
            data = await response.json();
            if (!response.ok) throw new Error(data);
            setShowBadge(data.added_candidate_stat_details?.added_candidate_unread_counts > 0);
        } catch (error) {
            setShowBadge(false);
        }
    };

    // Company API call function
    const fetchUnreadScoutCounts = async (authKey) => { //List scouting invoices
        let data;
        try {
            const response = await fetch(`${ServerURL.PRODUCTION_HOST_URL}/companies/list_scouting_invoices`, {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${authKey}`,
                },
            });
            data = await response.json();
            if (!response.ok) throw new Error(data);
            setUnreadScoutCount(data.invoices_list.length);
            setIsViewedByCompany(data.invoices_list?.is_viewed_by_company || 'No');
        } catch (error) {
            setUnreadScoutCount(0);
            setIsViewedByCompany('No');
        }
    };

    // Company API call function
    const fetchUnreadInvoiceCounts = async (authKey) => { //Unread invoice counts
        let data;
        try {
            const response = await fetch(`${ServerURL.PRODUCTION_HOST_URL}/companies/get_unread_invoice_count`, {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${authKey}`,
                },
            });
            data = await response.json();
            if (!response.ok) throw new Error(data);
            setSocutUnreadCount(data?.unread_invoice_count.scout_unread_count);
            setHiringUnreadCount(data?.unread_invoice_count.hiring_unread_count);
        } catch (error) {
            setSocutUnreadCount(0);
            setHiringUnreadCount(0);
        }
    };

    return (
        <UnreadCountContext.Provider
            value={{
                scoutPendingActions,
                isAlertModalCount,
                setScoutPendingActions,
                setIsAlertModalCount,
                scoutId,
                setScoutId,
                headerUnreadChatCounts,
                unreadChatCount,
                unreadNotificationCount,
                unreadScoutCount,
                showBadge,
                unreadSecretEntryCount,
                handleRefreshUnreadCountAPI,
                fetchUnreadChatCounts,
                unassigned_job_consultant_request_count,
                unassigned_scout_pending_count,
                unassigned_companies_count,
                unassigned_jobs_count,
                unread_hiring_count,
                unread_scouting_count,
                isViewedByCompany,
                socutUnreadCount,
                hiringUnreadCount,
                notificationsLists,
                notificationListsError,
                handleAdminRefreshUnreadCountAPI
            }}
        >
            {children}
        </UnreadCountContext.Provider>
    );
};
